import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, toHandlers as _toHandlers, mergeProps as _mergeProps, createBlock as _createBlock, KeepAlive as _KeepAlive, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode } from "vue"


const __default__ = {
  name: 'AppDialogProvider'
}

import { ref, provide, markRaw, computed } from 'vue'
import type { Component } from 'vue'
import { NConfigProvider, NModal } from 'naive-ui'
import { providerKey } from './context'
import { dialogComponents } from '../multiDialogs'
import type {
  ShowOptions,
  DialogProvider,
  ComponentParams,
  BackParams
} from './types'
import { pick } from 'lodash-es'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const DEFAULT_CONFIG = {
  maskClosable: false, // 点击遮罩是否关闭弹窗
  hideMask: false, // 是否隐藏遮罩
  componentProps: {},
  modalProps: {},
  componentEvents: {}
}

const refComponent = ref<null>(null)
const dialogList = ref<ComponentParams[][]>([] as ComponentParams[][])
const current = ref<ComponentParams>({} as ComponentParams)

// 获取数组中最后一个值
const getLastItem = (options: ComponentParams[]) => {
  return options.slice(-1)[0]
}

// 从列表删除数据，默认删除列表中的最后一个数组，传入delLastValue:true,则是删除最后一个数组中的最后一个值
const removeComponentFromCache = (
  firstStartIdx: number,
  secondStartIdx: number
) => {
  try {
    const lastArrayLength = dialogList.value?.slice(-1)[0].length

    if (firstStartIdx < 0) {
      firstStartIdx = dialogList.value.length + firstStartIdx
    }
    if (secondStartIdx < 0) {
      secondStartIdx = lastArrayLength + secondStartIdx
    }
    // 如果只有一个弹框，则关闭此弹框的最后一个内容，否则关闭当前弹框
    if (firstStartIdx === 0 && secondStartIdx >= 0) {
      dialogList.value = [dialogList.value[0]?.slice(0, secondStartIdx)]
    }
    if (firstStartIdx > 0 && firstStartIdx <= dialogList.value.length) {
      if (secondStartIdx > 0 && secondStartIdx <= lastArrayLength) {
        dialogList.value
          .at(-1)
          ?.splice(secondStartIdx, lastArrayLength - secondStartIdx)
      } else {
        dialogList.value = dialogList.value?.slice(0, firstStartIdx)
      }
    }
  } catch (error) {
    console.log('remove error', error)
  }
}

const show = async (params: ShowOptions) => {
  const { name, component: paramsComponent, isNested, componentKey } = params
  const _componentKey = componentKey || name || 'common'
  let definedComponent
  let component = paramsComponent
  if (!component && name) {
    definedComponent = dialogComponents[name]
    component = definedComponent?.body
  }
  if (!component) {
    console.error(`需要传入component 或在 dialog/index 注册组件并传入key`)
    return
  }
  const paramsKeys = [
    'modalProps',
    'componentProps',
    'componentKey',
    'componentEvents',
    'onClose',
    'maskClosable',
    'hideMask'
  ]
  const componentParams = pick(definedComponent, paramsKeys)
  const showParams = pick(params, paramsKeys)
  const config = Object.assign({}, DEFAULT_CONFIG, componentParams, showParams)

  // 获取异步组件里的组件
  // @ts-ignore
  if (component.__asyncLoader) {
    // @ts-ignore
    component = (await component.__asyncLoader()) as Component
  }

  current.value = {
    component: markRaw(component),
    ...config,
    visible: false,
    componentKey: _componentKey
  }

  // 如果弹框允许嵌套,默认嵌套一层
  if (
    !isNested &&
    dialogList.value.length &&
    dialogList.value[dialogList.value.length - 1].length
  ) {
    // 不允许嵌套，则将上一个弹框visible置为false,
    dialogList.value[dialogList.value.length - 1].slice(-1)[0].visible = false
  }
  // 如果列表中已经存在该弹框，则直接将visible置为true，且删除所在子数组内后面的弹框，否则新添加
  if (componentKey) {
    let firstIdx, secondIdx
    let tempList: ComponentParams[] = []
    dialogList.value.forEach((items, idx) => {
      firstIdx = idx
      tempList = items.filter((item, idx) => {
        secondIdx = idx
        return componentKey === item.componentKey
      })
    })
    if (!tempList.length) {
      current.value.visible = true
      // 若不允许嵌套且列表已经存在弹框，则在已存在的弹框数组后面push，否则新添加一个数组
      dialogList.value.length > 0 && !isNested
        ? dialogList.value?.slice(-1)[0].push(current.value)
        : dialogList.value.push([current.value])
    } else {
      dialogList.value[firstIdx][secondIdx].visible = true
      // 删除列表后面的弹框
      removeComponentFromCache(firstIdx, secondIdx)
    }
    return {
      _componentKey
    }
  }
  return {
    _componentKey
  }
}
// 关闭当前弹框
const close = () => {
  removeComponentFromCache(-1, -1)
  // 如果当前列表没有可视弹框，则清空列表
  let visibleDialogList: ComponentParams[] = []
  dialogList.value.forEach(
    items => (visibleDialogList = items.filter(item => item.visible))
  )
  if (!visibleDialogList.length) {
    closeAll()
  }
}

// 关闭全部
const closeAll = () => {
  dialogList.value = []
}

/**
 * 如果当前弹框是允许嵌套的，则返回的是上一个数组的弹框（关闭当前弹框），
 * 不允许嵌套则返回当前子数组内的上一个弹框（替换内容）
 */
const back = (params = {} as BackParams) => {
  try {
    const { componentProps } = params
    const fistTargetIndex = dialogList.value.length - 2
    const secondTargetIndex = dialogList.value?.slice(-1)[0].length - 2
    if (fistTargetIndex < 0 && secondTargetIndex < 0) {
      closeAll()
      return
    }
    if (secondTargetIndex < 0) {
      // 传回数据时，则赋值新数据
      if (
        dialogList.value[fistTargetIndex][secondTargetIndex] &&
        componentProps
      ) {
        dialogList.value[fistTargetIndex][secondTargetIndex].componentProps = {
          ...dialogList.value[fistTargetIndex][secondTargetIndex]
            .componentProps,
          ...{ componentProps }
        }
      }
      // 返回到上一组弹框
      removeComponentFromCache(fistTargetIndex - 1, -1)
    } else {
      // 返回本弹框的上一个内容
      if (dialogList.value.length) {
        dialogList.value[dialogList.value.length - 1][
          secondTargetIndex
        ].visible = true
        dialogList.value[dialogList.value.length - 1][
          secondTargetIndex + 1
        ].visible = false

        // 传回数据时，则赋值新数据
        if (
          dialogList.value[dialogList.value.length - 1][secondTargetIndex] &&
          componentProps
        ) {
          dialogList.value[dialogList.value.length - 1][
            secondTargetIndex
          ].componentProps = {
            ...dialogList.value[dialogList.value.length - 1][secondTargetIndex]
              .componentProps,
            ...componentProps
          }
        }
      }

      removeComponentFromCache(fistTargetIndex + 1, -1)
    }
  } catch (error) {
    console.log('dialog back error:', error)
  }
}

const provider: DialogProvider = {
  visible: computed(() =>
    dialogList.value.some(items => items.some(item => item.visible))
  ),
  show,
  close,
  closeAll,
  back
}

provide(providerKey, provider)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _renderSlot(_ctx.$slots, "default"),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dialogList.value, (options, idx) => {
      return (_openBlock(), _createBlock(_unref(NConfigProvider), {
        key: idx,
        namespace: `app-dialog-box ${
      getLastItem(options)!.componentKey
        ? 'app-dialog-' + getLastItem(options)!.componentKey
        : ''
    } ${getLastItem(options)!.hideMask ? 'app-dialog-mask--hide' : ''}`,
        abstract: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(NModal), _mergeProps({
            show: getLastItem(options)!.visible,
            "onUpdate:show": ($event: any) => ((getLastItem(options)!.visible) = $event),
            "mask-closable": getLastItem(options)!.maskClosable
          }, getLastItem(options)!.modalProps, {
            "transform-origin": "center",
            style: getLastItem(options)!.modalStyle
          }), {
            default: _withCtx(() => [
              _createElementVNode("div", null, [
                (_openBlock(), _createBlock(_KeepAlive, {
                  include: options.map(option => option.component.name || '')
                }, [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(getLastItem(options)!.component), _mergeProps({
                    key: getLastItem(options)!.componentKey
                  }, getLastItem(options)!.componentProps, _toHandlers(getLastItem(options)!.componentEvents), {
                    ref_for: true,
                    ref_key: "refComponent",
                    ref: refComponent,
                    onClose: close,
                    onCloseAll: closeAll,
                    onBack: back
                  }), null, 16))
                ], 1032, ["include"]))
              ])
            ]),
            _: 2
          }, 1040, ["show", "onUpdate:show", "mask-closable", "style"])
        ]),
        _: 2
      }, 1032, ["namespace"]))
    }), 128))
  ], 64))
}
}

})